import {
  Button,
  Checkbox,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useBoolean,
  useDisclosure,
} from '@chakra-ui/react';
import { ReactComponent as AccessibilityIcon } from 'assets/icons/accessibility.svg';
import { ReactComponent as GenerateIcon } from 'assets/icons/design_ai.svg';
import { ReactComponent as OutlineInfoIcon } from 'assets/icons/outline_info.svg';
import { ALTERNATIVE_TEXT_HELP_LINK } from 'constants/links';
import { ALT_TEXT_EDITED, ALT_TEXT_GENERATED, ALT_TEXT_GENERATION_ERROR, HELP_OPENED } from 'constants/mixpanel';
import { useAltTextGeneration } from 'hooks/useAltTextGeneration';
import { useRelativeWidgetPosition } from 'hooks/useRelativeWidgetPosition';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { store, useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectHasEditedAltText } from 'modules/Editor/store/editorSettingsSelector';
import {
  dangerouslySetHasKeyboardIgnore,
  setHasEditedAltText,
  setShowAltTagOnCanvas,
} from 'modules/Editor/store/editorSettingsSlice';
import { useHistory } from 'modules/Editor/store/history/historyManager';
import { selectAltText, selectIsDecorative } from 'modules/Editor/store/infographSelector';
import { updateWidget } from 'modules/Editor/store/infographSlice';
import { LoadingButton } from 'modules/common/components/Button/LoadingButton';
import { GenerationDiscardModal } from 'modules/common/components/GenerationDiscardModal';
import { ToastContent, useToast } from 'modules/common/components/Toast';
import { Toggletip } from 'modules/common/components/Toggletip';
import { ChangeEvent, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { customStyles } from 'theme/custom';

import { GENERATION_DISCARD_TEXT, MAX_ALLOW_CHARS, MODAL_WIDTH } from './AltTextModal.config';
import { AltTextModalContentProps, AltTextModalProps } from './AltTextModal.types';

export const AltTextModal = ({ isOpen, onClose, widgetId }: AltTextModalProps) => {
  const { isOpen: openDiscardModalFlag, onOpen: onOpenDiscardModal, onClose: onCloseDiscardModal } = useDisclosure();
  const hasGeneratedText = useRef(false);
  const dispatch = useAppDispatch();

  const handleOpen = useCallback(() => {
    dispatch(dangerouslySetHasKeyboardIgnore({ hasKeyboardIgnore: true }));
  }, [dispatch]);

  const handleClose = useCallback(
    (eventOrForceClose?: React.MouseEvent<HTMLButtonElement> | boolean) => {
      let forceClose = false;

      if (typeof eventOrForceClose === 'boolean') {
        forceClose = eventOrForceClose;
      }

      // Show discard confirmation modal if there is a text generation
      if (hasGeneratedText.current && !forceClose) {
        onOpenDiscardModal();
      } else {
        hasGeneratedText.current = false;
        dispatch(dangerouslySetHasKeyboardIgnore({ hasKeyboardIgnore: false }));
        onClose();
      }
    },
    [onOpenDiscardModal, dispatch, onClose],
  );

  const handleForceClose = useCallback(() => handleClose(true), [handleClose]);

  const onGenerateText = useCallback(() => (hasGeneratedText.current = true), []);
  const onDiscardText = useCallback(() => (hasGeneratedText.current = false), []);

  // Check if AltTextModal if is open, or close. If its open restrict keyboard shortcut, else modal is closed it allows keyboard shortcut
  useEffect(() => {
    isOpen === true ? handleOpen() : handleClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} portalProps={{ appendToParentPortal: false }} onClose={handleClose}>
      <ModalOverlay bg='blackAlpha.200' />
      <AltTextModalContent
        widgetId={widgetId}
        onClose={handleClose}
        onGenerateText={onGenerateText}
        onDiscardText={onDiscardText}
      />
      <GenerationDiscardModal
        isOpen={openDiscardModalFlag}
        onClose={onCloseDiscardModal}
        onDiscard={handleForceClose}
        fromParent={GENERATION_DISCARD_TEXT}
      />
    </Modal>
  );
};

const AltTextModalContent = memo(({ widgetId, onClose, onGenerateText, onDiscardText }: AltTextModalContentProps) => {
  const { t } = useTranslation('editor_toolbar_alt_text_menu', { useSuspense: false, keyPrefix: 'modal' });
  const toast = useToast();
  const { undo } = useHistory();
  const dispatch = useAppDispatch();
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { isGenerating, generateAltText, enabledGeneration } = useAltTextGeneration(widgetId);
  const [hasGeneratedText, setHasGeneratedText] = useBoolean(false);

  useRelativeWidgetPosition({
    widgetId,
    elementToPositionRef: modalContentRef,
    placement: 'right',
    scrollIntoViewOptions: { block: 'nearest', inline: 'center' },
    threshold: [MODAL_WIDTH, MODAL_WIDTH],
  });

  const altText = useAppSelector(selectAltText(widgetId));
  const isDecorative = useAppSelector(selectIsDecorative(widgetId));

  const [altTextInput, setAltTextInput] = useState<string>(altText);
  const [isDecorativeChecked, setIsDecorativeChecked] = useState<boolean>(isDecorative);

  const handleSubmit = () => {
    // If this is the first time the user has edited alt text, automatically show the alt tag on the canvas
    const storeState = store.getState();
    const hasUpdatedAltText = selectHasEditedAltText(storeState);
    if (!hasUpdatedAltText) {
      dispatch(setHasEditedAltText(true));
      dispatch(setShowAltTagOnCanvas(true));
    }

    const widgetData = { altText: isDecorativeChecked ? '' : altTextInput, isDecorative: isDecorativeChecked };
    dispatch(updateWidget({ widgetId, widgetData }));

    onClose(true);

    const toastId = `alt-text-added-${widgetId}`;
    const handleUndo = () => {
      if (undo) undo();
      toast.close(toastId);
    };

    toast({
      id: toastId,
      title: t('savedToast'),
      render: ({ title }) => <ToastContent title={title} buttonTitle={t('undo')} handleClick={handleUndo} />,
    });

    Mixpanel.track(ALT_TEXT_EDITED, {
      alt_text_value: isDecorative ? 'Marked as Decorative' : altText,
    });
  };

  const handleGenerateAltText = useCallback(async () => {
    // implementation
    const result = await generateAltText();
    if (result?.altText) {
      const { altText, type } = result;
      setAltTextInput(altText);
      setHasGeneratedText.on();

      Mixpanel.track(ALT_TEXT_GENERATED, {
        generated_alt_text: altText,
        type,
      });
      onGenerateText?.();
    } else {
      toast({
        title: t('designAi.error'),
        status: 'error',
      });

      Mixpanel.track(ALT_TEXT_GENERATION_ERROR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateAltText, onGenerateText, toast, t]);

  const onTextInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    // Re-allow generation if text is cleared
    if (e.target.value === '') {
      setHasGeneratedText.off();
    }

    setAltTextInput(e.target.value);
  };

  const onCheckIsDecorative = () => {
    setIsDecorativeChecked(!isDecorativeChecked);
    onDiscardGeneration();
  };

  const onDiscardGeneration = () => {
    setHasGeneratedText.off();
    onDiscardText?.();
    setAltTextInput('');
  };

  const handleClickHelpLink = () => {
    Mixpanel.track(HELP_OPENED, {
      from: 'Alt Modal',
      help_type: 'Alt Text',
    });
  };

  // STRINGS
  const modalTitle = t('heading');
  const description = t('description');
  const helpLinkText = t('helpLinkText');

  const generateLabel = t('designAi.label');
  const generateButtonLabel = t('designAi.buttonLabel');
  const generatingLabel = t('designAi.generating');

  const altTextPlaceholder = isDecorativeChecked
    ? t('textInputPlaceholder.disabled')
    : t('textInputPlaceholder.active');
  const markAsDecorativeLabel = t('markAsDecorative.label');
  const markAsDecorativeTooltip = t('markAsDecorative.tooltip');
  const markAsDecorativeTooltipAriaLabel = t('markAsDecorative.tooltipAriaLabel');

  const cancelLabel = t('cancel');
  const saveLabel = t('save');

  const isGenerationDisabled = hasGeneratedText || isDecorativeChecked;

  return (
    <ModalContent
      position='absolute'
      mr={2}
      ml={2}
      mt={0}
      mb={2}
      borderLeft={customStyles.container.violetGradient.borderLeft}
      background={customStyles.container.violetGradient.background}
      ref={modalContentRef}
      maxW='28.25rem'
    >
      <ModalHeader>
        <Flex alignItems='center' gap={2} color='violet.700'>
          <AccessibilityIcon aria-hidden={true} />
          <Text ml={2} fontWeight='600' fontSize='md' color='font.500'>
            {modalTitle}
          </Text>
        </Flex>
      </ModalHeader>
      <ModalBody>
        <Flex direction='column' gap={4} fontSize='sm' color='font.500'>
          {/* ALT TEXT DESCRIPTION */}
          <Flex direction='column' gap={2}>
            <Text>{description}</Text>
            <Link
              onClick={handleClickHelpLink}
              variant='inline'
              fontWeight='600'
              fontSize='xs'
              href={ALTERNATIVE_TEXT_HELP_LINK}
              isExternal
            >
              {helpLinkText}
            </Link>
          </Flex>

          {/* GENERATE ALT TEXT BUTTON */}
          {enabledGeneration && (
            <Flex
              justifyContent={'space-between'}
              bg='violet.50'
              borderRadius='base'
              p={2}
              alignItems={'center'}
              opacity={isGenerationDisabled ? 0.5 : 1}
            >
              <Flex color='violet.700' alignItems='center' gap={2}>
                <GenerateIcon />
                <Text fontWeight='500' fontSize='sm'>
                  {generateLabel}
                </Text>
              </Flex>
              <LoadingButton
                variant='purple'
                size='sm'
                onClick={handleGenerateAltText}
                isLoading={isGenerating}
                isDisabled={isGenerationDisabled}
                loadingText={generatingLabel}
                borderRadius='base'
                data-testid='alttext-generate-button'
                _disabled={{ opacity: 1, cursor: 'not-allowed' }}
              >
                {generateButtonLabel}
              </LoadingButton>
            </Flex>
          )}

          {/* ALT TEXT INPUT */}
          <Textarea
            maxLength={MAX_ALLOW_CHARS}
            fontSize={'sm'}
            placeholder={altTextPlaceholder}
            resize={'vertical'}
            p={2}
            value={isDecorativeChecked ? altTextPlaceholder : altTextInput}
            onChange={onTextInputChange}
            isDisabled={isDecorativeChecked || isGenerating}
            variant={isDecorativeChecked ? 'disabled' : 'outline'}
            focusBorderColor={'upgrade.blue.500'}
            data-testid={'alttext-text-input'}
            _disabled={{
              bg: 'blackAlpha.50',
              border: '1px solid',
              borderColor: 'gray.200',
              color: 'font.400',
            }}
            _placeholder={{ color: 'font.300' }}
          />

          {/* MARK AS DECORATIVE OPTION */}
          <Flex justifyContent={'space-between'}>
            <Flex gap={2} alignItems={'center'}>
              <Checkbox size={'sm'} isChecked={isDecorativeChecked} color={'font.500'} onChange={onCheckIsDecorative}>
                {markAsDecorativeLabel}
              </Checkbox>
              <Toggletip
                label={markAsDecorativeTooltip}
                placement={'top'}
                buttonAriaLabel={markAsDecorativeTooltipAriaLabel}
                hasArrow
                icon={<OutlineInfoIcon stroke={'#0069D1'} color={'#0069D1'} />}
                testLabel={'decorative-toggletip-button'}
              />
            </Flex>
          </Flex>
        </Flex>
      </ModalBody>
      <ModalFooter pt={0}>
        <Button
          borderRadius='base'
          variant='ghost'
          size='sm'
          mr={2}
          onClick={onClose}
          data-testid='alttext-cancel-button'
        >
          {cancelLabel}
        </Button>
        <Button
          borderRadius='base'
          colorScheme='green'
          size='sm'
          onClick={handleSubmit}
          data-testid='alttext-save-button'
        >
          {saveLabel}
        </Button>
      </ModalFooter>
    </ModalContent>
  );
});

AltTextModalContent.displayName = 'AltTextModalContent';
